// src/components/mfa/MfaConfirmModal.jsx
//
// Password plus a code, for the sensitive two-step actions: turning it off and
// making new recovery codes. The code may be a 6-digit code from the app or a
// recovery code (the server accepts either). With requireCode false it asks for
// the password only: cancelling a setup that was never finished.
import React, { useState } from 'react';
import { KeyRound, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { isCompleteCode, sanitizeAnyCodeInput } from '../../lib/mfa';
import MfaModalShell, { ErrorBanner } from './MfaModalShell';

export function ConfirmFields({ password, code, requireCode, error, onPasswordChange, onCodeChange, onSubmit, ready }) {
  const submitOnEnter = (e) => { if (e.key === 'Enter' && ready) onSubmit?.(); };
  return (
    <>
      <ErrorBanner message={error} />
      <div>
        <Label htmlFor="mfa-confirm-password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
          <Input
            id="mfa-confirm-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            onKeyDown={submitOnEnter}
            className="pl-10"
          />
        </div>
      </div>
      {requireCode && (
        <div>
          <Label htmlFor="mfa-confirm-code">Code from your app, or a recovery code</Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
            <Input
              id="mfa-confirm-code"
              type="text"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="123456"
              value={code}
              onChange={(e) => onCodeChange(sanitizeAnyCodeInput(e.target.value))}
              onKeyDown={submitOnEnter}
              className="pl-10 font-mono"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default function MfaConfirmModal({
  icon = KeyRound, tone = 'blue', title, subtitle, confirmText, confirmVariant = 'default',
  requireCode = true, onSubmit, onCancel,
}) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const ready = Boolean(password) && (!requireCode || isCompleteCode(code, 'any'));

  // onSubmit(password, code) resolves to an error message, or null on success
  // (the parent closes the dialog on success).
  const handleSubmit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    const message = await onSubmit(password, requireCode ? code : '');
    setBusy(false);
    if (message) {
      setError(message);
      setCode('');
    }
  };

  return (
    <MfaModalShell
      icon={icon}
      tone={tone}
      title={title}
      subtitle={subtitle}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant={confirmVariant} onClick={handleSubmit} disabled={busy || !ready}>
            {busy ? 'Working...' : confirmText}
          </Button>
        </div>
      )}
    >
      <ConfirmFields
        password={password}
        code={code}
        requireCode={requireCode}
        error={error}
        onPasswordChange={setPassword}
        onCodeChange={setCode}
        onSubmit={handleSubmit}
        ready={ready}
      />
    </MfaModalShell>
  );
}
